import React, { useState } from "react";
import { Kol, kolService, SortBy, SortOrder } from "../services/kol.service";
import { Button, message, Pagination, Table, Tag, theme } from "antd";
import type { TableProps } from "antd";
import AddKolsModal from "../components/AddKolsModal";
import { ReloadOutlined } from "@ant-design/icons";
import { useKols } from "../hooks/useKols";

const KolManagement: React.FC = () => {
  const {
    token: { colorBgContainer, colorBorderSecondary },
  } = theme.useToken();

  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<SortBy | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<SortOrder | undefined>(undefined);
  const [deleteIdsLoading, setDeleteIdsLoading] = useState<
    Record<string, boolean>
  >({});

  const { data, isLoading, refetch, isRefetching } = useKols({
    page,
    limit: 100,
    sortBy,
    sortOrder,
  });

  const handleDelete = async (id: string) => {
    try {
      setDeleteIdsLoading((prev) => ({ ...prev, [id]: true }));
      await kolService.deleteKols(id);
      delete deleteIdsLoading[id];
      refetch();
    } catch (error) {
      message.error("Failed to delete KOL");
    } finally {
      delete deleteIdsLoading[id];
    }
  };

  const columns: TableProps<Kol>["columns"] = [
    {
      title: "",
      dataIndex: "id",
      key: "id",
      render: (value: string, record: Kol, index: number) =>
        index + 1 + (page - 1) * 100,
    },
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      render: (value: string) =>
        value?.length > 8 ? `${value.slice(0, 8)}...` : value,
    },
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      render: (value: string) => (
        <a
          href={`https://www.x.com/${value}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {value?.length > 12 ? `${value.slice(0, 8)}...` : value}
        </a>
      ),
    },
    {
      title: "Display Name",
      dataIndex: "displayName",
      key: "displayName",
      render: (value: string) =>
        value?.length > 8 ? `${value.slice(0, 8)}...` : value,
    },
    {
      title: "Crawled",
      dataIndex: "lastCrawledAt",
      key: "lastCrawledAt",
      sorter: true,
      sortOrder:
        sortBy === SortBy.LAST_CRAWLED_AT
          ? sortOrder === SortOrder.ASC
            ? "ascend"
            : "descend"
          : null,
      render: (value: number) => {
        if (!value) return "-";
        const secondsAgo = Math.floor(Date.now() / 1000) - value;
        return `${secondsAgo}s ago`;
      },
    },
    {
      title: "Upserted Count",
      dataIndex: "lastUpsertedCount",
      key: "lastUpsertedCount",
      sorter: true,
      sortOrder:
        sortBy === SortBy.LAST_UPSERTED_COUNT
          ? sortOrder === SortOrder.ASC
            ? "ascend"
            : "descend"
          : null,
      render: (value: number) => {
        return (
          <Tag color={value > 0 ? "green" : "red"}>
            {value ?? 0} tweet{value > 1 ? "s" : ""}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: Kol) => (
        <Button
          danger
          onClick={() => handleDelete(record.id)}
          loading={deleteIdsLoading[record.id]}
          size="small"
          type="text"
        >
          Delete
        </Button>
      ),
    },
  ];

  const handleTableChange: TableProps<Kol>["onChange"] = (
    pagination,
    filters,
    sorter
  ) => {
    if (sorter && "field" in sorter) {
      setSortBy(sorter.field as SortBy);
      setSortOrder(sorter.order === "ascend" ? SortOrder.ASC : SortOrder.DESC);
    } else {
      setSortBy(undefined);
      setSortOrder(undefined);
    }
  };

  return (
    <div className="relative">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "sticky",
          top: 0,
          zIndex: 100,
          background: colorBgContainer,
          borderBottom: `1px solid ${colorBorderSecondary}`,
        }}
      >
        <h2>KOL Management</h2>
        <AddKolsModal onSuccess={() => refetch()} />
      </div>

      <div
        style={{
          padding: "16px 0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span>{data?.totalDocs || 0} KOLs found</span>
          <Button
            icon={<ReloadOutlined style={{ fontSize: 12, marginBottom: 2 }} />}
            type="default"
            size="small"
            onClick={() => refetch()}
            loading={isRefetching}
          >
            Refresh
          </Button>
        </div>

        <Pagination
          current={page}
          total={data?.totalDocs}
          pageSize={100}
          onChange={(page) => setPage(page)}
        />
      </div>

      <Table
        rowKey="id"
        dataSource={data?.docs}
        columns={columns}
        loading={isLoading}
        onChange={handleTableChange}
        pagination={false}
      />
    </div>
  );
};

export default KolManagement;
